import { useTranslation } from 'react-i18next';
import { Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, useDisclosure } from '@heroui/modal';
import { Button } from '@heroui/button';
import { Form } from '@heroui/form';
import { Input } from '@heroui/input';
import { FormEvent, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TbTrashFilled } from 'react-icons/tb';

import { Song } from '@/core/db.ts';
import { useEditSong, useRemoveSong } from '@/hooks/useSongbook.ts';

interface SongEditModalProps {
  disclosure: ReturnType<typeof useDisclosure>;
  song: Song;
}

function SongEditModal({ disclosure, song }: SongEditModalProps) {
  const { t } = useTranslation();
  const { isOpen, onOpenChange, onClose } = disclosure;
  const { mutateAsync: editSong, isPending } = useEditSong(song.id);
  const { mutateAsync: removeSong } = useRemoveSong(song);
  const navigate = useNavigate();
  const [isRemoving, setIsRemoving] = useState(false);

  const handleSubmit = useCallback(async (e: FormEvent) => {
    e.preventDefault();

    const data = new FormData(e.target as HTMLFormElement);

    const fields = ['title', 'artist', 'key', 'tempo'];

    await editSong(
      fields
        .reduce((acc, f) => {
          const value = data.get(f)?.toString();

          return {
            ...acc,
            [f]: value,
          };
        }, {})
    );
    onClose();
  }, [onClose, editSong]);

  const handleRemove = useCallback(async () => {
    await removeSong();
    navigate(`/songbook/${song.songbookId}`);
  }, [removeSong, navigate, onClose, song.songbookId]);

  return (
    <Modal backdrop="blur" isOpen={isOpen} onOpenChange={onOpenChange}>
      <ModalContent className="bg-default-50 text-foreground">
        {(onClose) => isRemoving ? (
          <div className="w-full">
            <ModalHeader className="flex flex-col gap-1">{t('songbook.modal.removeSongTitle')}</ModalHeader>
            <ModalBody>
              {t('songbook.modal.removeMessage', { title: song.title })}
            </ModalBody>
            <ModalFooter>
              <Button color="default" disabled={isPending} onPress={() => setIsRemoving(false)}>
                {t('songbook.modal.cancel')}
              </Button>
              <Button className="text-foreground" color="danger" disabled={isPending} onPress={handleRemove}>
                {t('songbook.modal.removeConfirm')}
              </Button>
            </ModalFooter>
          </div>
          ) : (
          <Form onSubmit={handleSubmit}>
            <div className="w-full">
              <ModalHeader className="flex flex-col gap-1">{t('songbook.modal.editSongTitle')}</ModalHeader>
              <ModalBody>
                <Input
                  isRequired
                  defaultValue={song.title}
                  disabled={isPending}
                  errorMessage={t('songbook.song.fields.title.error')}
                  label={t('songbook.song.fields.title.label')}
                  labelPlacement="outside"
                  name="title"
                  placeholder={t('songbook.song.fields.title.placeholder')}
                  type="text"
                />

                <Input
                  defaultValue={song.artist}
                  disabled={isPending}
                  label={t('songbook.song.fields.artist.label')}
                  labelPlacement="outside"
                  name="artist"
                  placeholder={t('songbook.song.fields.artist.placeholder')}
                  type="text"
                />

                <div className="flex gap-4">
                  <Input
                    defaultValue={song.key}
                    disabled={isPending}
                    label={t('songbook.song.fields.key.label')}
                    labelPlacement="outside"
                    name="key"
                    placeholder={t('songbook.song.fields.key.placeholder')}
                    type="text"
                  />
                  <Input
                    defaultValue={song.tempo?.toString()}
                    disabled={isPending}
                    label={t('songbook.song.fields.tempo.label')}
                    labelPlacement="outside"
                    name="tempo"
                    placeholder={t('songbook.song.fields.tempo.placeholder')}
                    type="number"
                  />
                </div>
              </ModalBody>
              <ModalFooter>
                <div className="grow">
                  <Button
                    className="text-foreground"
                    color="danger"
                    disabled={isPending}
                    startContent={<TbTrashFilled />}
                    onPress={() => setIsRemoving(true)}
                  >
                    {t('songbook.modal.remove')}
                  </Button>
                </div>
                <Button color="default" disabled={isPending} variant="light" onPress={onClose}>
                  {t('songbook.modal.cancel')}
                </Button>
                <Button color="primary" disabled={isPending} type="submit">
                  {t('songbook.modal.update')}
                </Button>
              </ModalFooter>
            </div>
          </Form>
        )}
      </ModalContent>
    </Modal>
  );
}

export default SongEditModal;
